import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarClock, CheckCircle2, FileSignature, Plus, Search, Truck } from "lucide-react";
import { api } from "../api/client";
import LoadStatusBadge from "../components/trucking/LoadStatusBadge";
import { PageHeaderBand } from "../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../lib/load-failure";
import { unwrapArray } from "../lib/api-envelope";
import { LOAD_STATUSES, LOAD_STATUS_BADGE, loadStatusSkin, type LoadStatus } from "../lib/load-status";
import { formatCalendarDate, instantTenantDay, todayCalendarDate } from "../lib/calendar-date";
import { getLoadTypeLabel } from "../constants/load-types";
import { useTenantTimeZone } from "../hooks/useTenantTimeZone";
import { displayValue } from "../features/fieldiq-handoff/contracts";

/**
 * Trucking Dispatch: every load, as GET /trucking/loads sends it.
 *
 * WHAT IT READ, AND WHAT IT READS NOW. The register went through
 * `useTruckingData`'s TruckLoad mapping, which this page cannot use honestly:
 * its driver came from `driver_name` (the route sends `driver_first_name` and
 * `driver_last_name`, so every row read "Unassigned"), its truck column printed
 * the first four characters of a UUID, its HOS bar drew a hard-coded 0 hours,
 * and its avatar colour was a placeholder. The route is unpaginated, so the
 * page reads the raw rows (`l.*` + truck_unit_number, customer_name and the
 * driver's names) and tallies them.
 *
 * THE BAND'S FOUR FIGURES are tallies of that list, which is unpaginated and
 * is also what the chips count, so band and chips cannot disagree. (Since
 * api#851 GET /trucking/dispatch/metrics agrees too: `active_loads` is booked
 * to arrived and `bol_pending` is delivered. The dashboard reads those.)
 *   - "To sign" is loads whose status is `delivered`: signing moves a
 *     load to `signed`, so a delivered load has not been signed. It is the
 *     same set as the API's `bol_pending`, and carries the same name the
 *     Overview gives that figure; "Unsigned" was also the BOL list's word for
 *     every load not yet signed (fix/trucking-figures-agree). Short on purpose:
 *     this is the band's third column, whose label has a ~70px ceiling, and
 *     the label and sub-line are no longer than "Unsigned" / "At delivery". The old "BOL
 *     PENDING SIGN" stat counted loads with no BOL number.
 *   - "Delivered today" is `delivered_at` (an instant) falling on the TENANT's
 *     today. The old figure counted every delivered load ever.
 *
 * DRIVER AVAILABILITY is a panel under the register, reading GET
 * /trucking/drivers/availability as it is sent (a bare array of active
 * drivers). The old alerts read `drivers` and `time_off` off it, which it never
 * carried, so they never rendered. Since api#851 `hos_remaining_hours` is null
 * for a driver with no hours-of-service log (it read a full 70 before), and
 * `cert_expires_soon` is null when the driver has no cert dates at all; each
 * shows as exactly that, never as a clean bill.
 *
 * GONE: the tab strip (four links to sidebar pages and a "Drivers" tab that
 * said "go to Drivers"); the expanded row with "Generate invoice", "Get
 * signature", "Print BOL PDF" and "View move board" buttons, none of which had
 * a handler; and the per-row action button, also with no handler. A row now
 * opens the load's BOL, which shows its cargo and signatures and has the real
 * PDF.
 */

interface DriverAvailability {
  id: string;
  name: string | null;
  status: string | null;
  current_bol_number: string | null;
  hos_remaining_hours: number | null;
  cert_expires_soon: boolean | null;
}
const toDriver = (r: Record<string, unknown>): DriverAvailability => ({
  id: String(r.id ?? ""),
  name: str(r.name),
  status: str(r.status),
  current_bol_number: str(r.current_bol_number),
  hos_remaining_hours: typeof r.hos_remaining_hours === "number" ? r.hos_remaining_hours : null,
  cert_expires_soon: typeof r.cert_expires_soon === "boolean" ? r.cert_expires_soon : null,
});

/**
 * The route sends `dispatched` (on a dispatched or en-route load) or
 * `available` for an active driver. Anything else is a slate dash: an
 * unrecognised status must not read as free to send.
 */
const DRIVER_STATUS: Record<string, { label: string; fill: string }> = {
  available: { label: "Available", fill: "bg-fi-signal-green" },
  dispatched: { label: "Dispatched", fill: "bg-fi-signal-blue" },
};
const driverStatusSkin = (s: string | null) =>
  s != null && Object.prototype.hasOwnProperty.call(DRIVER_STATUS, s) ? DRIVER_STATUS[s] : { label: "—", fill: "bg-fi-signal-slate" };

/**
 * Certs: `cert_expires_soon` is true for a licence or cert that expires within
 * 30 of the tenant's days OR HAS ALREADY EXPIRED (the API's test is
 * `expiry <= today + 30`), false when all are clear, null with no dates.
 * "Renewal due" therefore described an expired licence as a renewal to book
 * (Mike Kowalski, expired 2026-08-31). Until the route says which, this names
 * both; it never claims "expired" or "not yet expired" it cannot see.
 */
const certLabel = (v: boolean | null) => (v === true ? "Expiring or expired" : v === false ? "Current" : "No dates on file");

interface LoadRow {
  id: string;
  bol_number: string | null;
  status: string | null;
  load_type: string | null;
  scheduled_date: string | null;
  pickup_location: string | null;
  delivery_location: string | null;
  customer_name: string | null;
  truck_id: string | null;
  truck_unit_number: string | null;
  driver_id: string | null;
  driver_name: string | null;
  delivered_at: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

function toRow(r: Record<string, unknown>): LoadRow {
  const name = [str(r.driver_first_name), str(r.driver_last_name)].filter(Boolean).join(" ");
  return {
    id: String(r.id ?? ""),
    bol_number: str(r.bol_number),
    status: str(r.status),
    load_type: str(r.load_type),
    scheduled_date: str(r.scheduled_date),
    pickup_location: str(r.pickup_location),
    delivery_location: str(r.delivery_location),
    customer_name: str(r.customer_name),
    truck_id: str(r.truck_id),
    truck_unit_number: str(r.truck_unit_number),
    driver_id: str(r.driver_id),
    driver_name: name || null,
    delivered_at: str(r.delivered_at),
  };
}

/** No driver on the load is "Unassigned"; a driver whose name did not arrive is a dash. */
const driverLabel = (l: LoadRow) => (l.driver_id == null ? "Unassigned" : l.driver_name ?? "—");
/**
 * The same for the truck. `truck_unit_number` is joined from `trucking_fleet`
 * (api#860); a truck whose unit number did not arrive is a dash, not "Unassigned".
 */
const truckLabel = (l: LoadRow) => (l.truck_id == null ? "Unassigned" : l.truck_unit_number ?? "—");

type ChipKey = "all" | LoadStatus;
const CHIPS: { key: ChipKey; label: string }[] = [
  { key: "all", label: "All" },
  ...LOAD_STATUSES.map((s) => ({ key: s as ChipKey, label: LOAD_STATUS_BADGE[s].label })),
];
const IN_PROGRESS = new Set(["dispatched", "en_route", "arrived"]);

/** A calendar day as a month name ("Oct 2, 2026"); a bare numeric day is ambiguous across locales. */
const fmtDay = (d: string | null) => formatCalendarDate(d) ?? "—";

const TruckingDispatch = () => {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const [chip, setChip] = useState<ChipKey>("all");
  const [search, setSearch] = useState("");

  // The key shares the `trucking-loads` prefix, so every load write's
  // invalidation refreshes this list too.
  const loadsQuery = useQuery({
    queryKey: ["trucking-loads", "dispatch-register"],
    queryFn: () => api.trucking.loads.list().then((r) => unwrapArray<Record<string, unknown>>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(loadsQuery) != null;
  const driversQuery = useQuery({
    queryKey: ["trucking-driver-availability"],
    queryFn: () => api.trucking.driverAvailability().then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toDriver)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const driversFailed = queryLoadFailure(driversQuery) != null;
  const drivers = driversFailed ? undefined : driversQuery.data;
  const loading = loadsQuery.isLoading && !failed;
  const loads = useMemo(() => (failed || loadsQuery.data == null ? undefined : loadsQuery.data.map(toRow)), [failed, loadsQuery.data]);

  /** Tallies of the loaded list: GET /trucking/loads is unpaginated. */
  const count = (match: (l: LoadRow) => boolean): number | null => (loads ? loads.filter(match).length : null);
  const countOf = (k: ChipKey) => count((l) => k === "all" || l.status === k);
  const fig = (n: number | null) => (failed ? "—" : displayValue(n));

  // The tenant's today (companies.time_zone). With no zone there is no today,
  // so the figure is a dash rather than a count against some other day.
  const today = todayCalendarDate(timeZone);
  const deliveredToday =
    today == null ? null : count((l) => l.delivered_at != null && instantTenantDay(l.delivered_at, timeZone) === today);

  const filtered = (loads ?? []).filter((l) => {
    if (chip !== "all" && l.status !== chip) return false;
    if (search) {
      const q = search.toLowerCase();
      return [l.bol_number, l.driver_name, l.customer_name, l.truck_unit_number].some((v) => (v ?? "").toLowerCase().includes(q));
    }
    return true;
  });

  const open = (id: string) => navigate(`/trucking/bols/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="trucking-dispatch-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="Dispatch"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate("/trucking/settings")}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              Settings
            </button>
            <button type="button" onClick={() => navigate("/trucking/new-load")} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New Load
            </button>
          </>
        }
        stats={[
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "Booked", sub: "Not dispatched", tone: "neutral", loading, value: fig(countOf("booked")) },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "In Progress", sub: "Dispatched to arrived", tone: "info", loading, value: fig(count((l) => IN_PROGRESS.has(l.status ?? ""))) },
          { icon: <FileSignature className="size-4" aria-hidden="true" />, label: "To sign", sub: "Delivered", tone: "accent", loading, value: fig(countOf("delivered")) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: "Delivered", sub: "Today", tone: "success", loading, value: fig(deliveredToday) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {CHIPS.map((c) => {
                const active = chip === c.key;
                const n = countOf(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setChip(c.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? "bg-fi-amber text-fi-amber-ink"
                        : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {c.label}
                    {!loading && !failed && n != null && (
                      <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                placeholder="Search BOL, driver, customer, truck..."
                aria-label="Search loads"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          <section
            aria-labelledby="load-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Load register</p>
              <h2 id="load-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Loads
              </h2>
            </header>

            {failed ? (
              <LoadFailureNotice error={queryFailureError(loadsQuery)} subject="loads" />
            ) : loading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                {search || chip !== "all" ? "No loads match these filters" : "No loads yet"}
              </p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1000px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">BOL</th>
                        <th className="px-4 py-3">Scheduled</th>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Route</th>
                        <th className="px-4 py-3">Driver</th>
                        <th className="px-4 py-3">Truck</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((l) => (
                        <tr
                          key={l.id}
                          data-testid="dispatch-load-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(l.id)}
                          onKeyDown={rowKeys(l.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${loadStatusSkin(l.status).rail}`}
                        >
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-bold text-fieldiq-text">{l.bol_number ?? "—"}</td>
                          {/* `scheduled_date` is a CALENDAR DAY. */}
                          <td className="whitespace-nowrap px-4 py-3 text-xs text-fieldiq-muted">{fmtDay(l.scheduled_date)}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-fieldiq-text">{l.customer_name ?? "—"}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
                              <span>{l.pickup_location ?? "—"}</span>
                              <ArrowRight className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                              <span>{l.delivery_location ?? "—"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{driverLabel(l)}</td>
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-fieldiq-text">{truckLabel(l)}</td>
                          <td className="px-4 py-3 text-xs text-fieldiq-muted">{getLoadTypeLabel(l.load_type)}</td>
                          <td className="px-4 py-3"><LoadStatusBadge status={l.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      data-testid="dispatch-load-card"
                      onClick={() => open(l.id)}
                      className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-bold text-fieldiq-text">{l.bol_number ?? "—"}</p>
                          <p className="truncate text-xs text-fieldiq-muted">{l.customer_name ?? "—"}</p>
                        </div>
                        <LoadStatusBadge status={l.status} />
                      </div>
                      <p className="mt-2 text-xs text-fieldiq-muted">
                        {l.pickup_location ?? "—"} → {l.delivery_location ?? "—"}
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-text">
                        {fmtDay(l.scheduled_date)} · {driverLabel(l)} · {truckLabel(l)}
                      </p>
                    </button>
                  ))}
                </div>
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {filtered.length} of {loads?.length ?? 0} loads
                </p>
              </>
            )}
          </section>

          <section
            aria-labelledby="driver-availability-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Active drivers</p>
              <h2 id="driver-availability-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Driver availability
              </h2>
            </header>
            {driversFailed ? (
              <LoadFailureNotice error={queryFailureError(driversQuery)} subject="driver availability" />
            ) : driversQuery.isLoading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2].map((i) => (
                  <div key={i} className="h-10 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : !drivers || drivers.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-fieldiq-muted">No active drivers</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    <tr className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                      <th className="px-4 py-3">Driver</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Current BOL</th>
                      <th className="px-4 py-3 text-right">HOS left (7-day)</th>
                      <th className="px-4 py-3">Certs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.map((d) => {
                      const skin = driverStatusSkin(d.status);
                      return (
                        <tr key={d.id} data-testid="driver-availability-row" className="border-t border-fieldiq-border">
                          <td className="px-4 py-3 text-sm font-semibold text-fieldiq-text">{d.name ?? "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-wider text-fi-signal-ink ${skin.fill}`}>
                              {skin.label}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-fieldiq-text">{d.current_bol_number ?? "—"}</td>
                          {/* Null: no hours-of-service log for this driver. Not a full cycle. */}
                          <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">
                            {d.hos_remaining_hours === null ? "—" : `${d.hos_remaining_hours}h`}
                          </td>
                          <td className="px-4 py-3 text-xs text-fieldiq-text">{certLabel(d.cert_expires_soon)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {drivers && drivers.length > 0 && drivers.some((d) => d.hos_remaining_hours === null) && (
              <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                A dash under HOS means no hours-of-service log is recorded for that driver.
              </p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
};

export default TruckingDispatch;
